import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { type AxiosError } from "axios";
import { useQueryParams } from "@/frontend/shared/lib/hooks/useQueryParams";
import { UserIcon } from "@/frontend/shared/assets/icons";
import { Hint } from "@/frontend/shared/ui/Hint/Hint";
import { Input } from "@/frontend/shared/ui/Input";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Typography } from "@/frontend/shared/ui/Typography";
import $api from "@/frontend/shared/api/axios";
import { SignUpBody, PasswordSchema, type TSignUpBody } from "@/frontend/shared/schemas/sign-up";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { getInviteTokenInfo } from "../model/services";
import { getRouteSignIn, getRouteSignUp } from "@/frontend/shared/const/router";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";

import styles from "./ApplicationFormSignUp.module.scss";

export const ApplicationFormSignUp = () => {
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const token = queryParams.get("token") as string;

  const [tokenError, setTokenError] = useState("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [organizationName, setOrganizationName] = useState<string>("");
  // const [isOrganizationPreFilled, setIsOrganizationPreFilled] = useState<boolean>(false);

  const validPassword = PasswordSchema.safeParse(password);

  const isPasswordLengthValid = password.length >= 8;
  const isPasswordHasOneNumber = password.match(/[0-9]/) !== null;
  const isPasswordHasOneCapitalLetter = password.match(/[A-Z]/) !== null;

  const hasValidInput = SignUpBody.safeParse({
    token,
    firstName,
    lastName,
    email,
    password,
    organizationName
  }).success;

  useQuery({
    queryKey: ["signUpData"],
    queryFn: async () => {
      try {
        const tokenInfo = await getInviteTokenInfo(token);
        setEmail(tokenInfo.email);
        setTokenError("");

        setFirstName(tokenInfo?.firstName ?? "");
        setLastName(tokenInfo?.lastName ?? "");

        if (tokenInfo?.entity === ERoleAccess.INVESTOR) {
          navigate(`${getRouteSignUp()}?token=${token}`, { replace: true });
        }

        // VC-VC invite - J
        if (tokenInfo?.companyName) {
          setOrganizationName(tokenInfo.companyName);
          // setIsOrganizationPreFilled(true);
        }

        // Innovera-VC invite - J
        if (tokenInfo?.investor?.name) {
          setOrganizationName(tokenInfo?.investor?.name);
          // setIsOrganizationPreFilled(true);
        }

        if (tokenInfo?.entity === ERoleAccess.EXPERT && tokenInfo?.organizationName) {
          setOrganizationName(tokenInfo?.organizationName);
          // setIsOrganizationPreFilled(true);
        }

        return tokenInfo;
      } catch (error) {
        const axiosError = error as AxiosError<{ message: string }>;
        console.error(axiosError);

        setTokenError("Token is invalid or expired");
        snackbarConstructor("Token is invalid or expired", "error");
      }
    },
    refetchOnWindowFocus: false
  });

  const mutation = useMutation({
    mutationKey: ["sign-up-invite"],
    mutationFn: async (payload: TSignUpBody) => {
      const response = await $api.post(`/auth/register?inviteToken=${token}`, payload);
      return response.data;
    },
    onSuccess: () => {
      window?.location?.reload();
    },
    onError: (err: AxiosError<{ error: string }>) => {
      const message =
        err.response?.data?.error ??
        err?.message ??
        "Failed to create account. Try again later or contact the administrator.";
      snackbarConstructor(message, "error");
      reportErrorToSentry(message, "/auth/register");
    }
  });

  const signUp = async () => {
    if (!hasValidInput) return;

    if (tokenError) {
      snackbarConstructor(tokenError, "error");
      return;
    }

    await mutation.mutateAsync({
      token,
      firstName,
      lastName,
      email,
      password,
      organizationName
    });
  };

  useEffect(() => {
    const env = import.meta.env.VITE_ENV;
    // Temporarily prevent production access - J
    if (env === "production") {
      navigate(getRouteSignIn());
    }
  }, [navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.subContainerLeft}>
        <img src="/logos/deutsche-telekom.png" alt="Deutsche Telekom" className={styles.logo} />;
        <Typography variant="heading-4" color="greyscale900">
          Deutsche Telekom
        </Typography>
        <Typography variant="body-1">Application Submission</Typography>
      </div>

      <div className={styles.subContainerRight}>
        <div className={styles.signUpForm}>
          <Typography variant="heading-5" style={{ marginBottom: "1rem", fontWeight: "normal" }}>
            One More Step to Get Started with Innovera!
          </Typography>
          <Typography variant="body-1" style={{ marginBottom: "2rem" }}>
            Manage your application
          </Typography>

          <div className={styles.formRow}>
            <div className={styles.userIconContainer}>
              <UserIcon className={styles.userIcon} />
              <div>
                <Typography variant="body-1" color="greyscale900">{`${firstName} ${lastName}`}</Typography>
                <Typography variant="body-1" color="greyscale500">
                  {email}
                </Typography>
              </div>
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Password
              </Typography>
              <Input
                type="password"
                id="password"
                name="password"
                className={styles.inputField}
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
              />

              <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", flexDirection: "column" }}>
                <Hint label="8 characters or more" isActive={isPasswordLengthValid} className={styles.hint} />
                <Hint label="At least 1 number" isActive={isPasswordHasOneNumber} className={styles.hint} />
                <Hint
                  label="At least 1 uppercase letter"
                  isActive={isPasswordHasOneCapitalLetter}
                  className={styles.hint}
                />
              </div>
            </div>
          </div>

          <NormalButton
            size="large"
            isDisabled={!hasValidInput || !validPassword || mutation.isPending}
            onClick={signUp}
          >
            {mutation.isPending ? "Submitting..." : "Sign Up"}
          </NormalButton>
        </div>
      </div>
    </div>
  );
};
